import { ProfileTypes } from '@api/Users/User';

// ============================================================================
// QUIÉN PUEDE QUÉ — el único lugar donde se decide un permiso.
//
// Lección de la casa: **un gate va sobre la función que ejecuta, no sobre los
// botones**. Hasta el 2026-09-27 los permisos eran ~33 comparaciones sueltas de
// `profile ===` en 16 archivos; cada rol nuevo obligaba a encontrarlas todas. Aquí
// la tabla de roles del negocio (planes/PLAN-GASTOS-Y-ADMIN-DE-SUCURSAL.md §4)
// ES el código, y `Capabilities.test.ts` la recorre renglón por renglón.
//
// Los chequeos viejos se migran aquí conforme se tocan; una prueba que lee el
// fuente (`profileChecks.test.ts`) impide agregar comparaciones nuevas fuera.
//
// ⚠️ Esto vive en el cliente, como todo el control de acceso del sistema
// (decisión vigente: sin rules, sin middleware en el server).
// ============================================================================

export enum Capability {
    // Gastos (F2–F5 del plan)
    ExpenseCaptureOperating = 'expenses.capture.operating',
    ExpenseCapturePayroll = 'expenses.capture.payroll',
    ExpenseCaptureFixed = 'expenses.capture.fixed',
    ExpenseEdit = 'expenses.edit',
    ExpenseCancel = 'expenses.cancel',
    ExpenseListView = 'expenses.list.view',
    RecurringExpenseManage = 'expenses.recurring.manage',
    ExpenseCategoriesManage = 'expenses.categories.manage',
    IncomeStatementView = 'incomeStatement.view',
    IncomeStatementConsolidated = 'incomeStatement.consolidated',

    // Administración de la sucursal (F6)
    UsersManage = 'users.manage',
    /** Borrar un usuario: los dos admins, cada uno en su alcance (regla de Irving, 2026-09-30). */
    UsersDelete = 'users.delete',
    BranchEdit = 'branch.edit',
    /** Lo de la sucursal que no es de la sucursal (§8.7): número (prefijo del folio), CEDIS, inventario, dar de alta. */
    BranchEditProtected = 'branch.edit.protected',
    PromoCodesManage = 'promoCodes.manage',
    /** Códigos de "Todas las sucursales": el admin de sucursal los ve, no los edita. */
    PromoCodesManageGlobal = 'promoCodes.manage.global',
    /** Dashboard (Órdenes + Cajas), acotado a las sucursales del usuario. */
    DashboardView = 'dashboard.view',
    /** El renglón del sidebar que abre la app de supervisión (https://supervision.krispis.mx). */
    SupervisionAppOpen = 'supervision.app.open',

    // Inventario de sucursal (plan de inventario F2)
    /** Conteo físico: hoy solo pide PIN; la capacidad existe para decidirlo en un solo lugar. */
    InventoryCount = 'inventory.count',
    /** Registrar merma. Cualquiera de la sucursal captura; el candado es el PIN de un gerente/admin (§8.4). */
    InventoryShrinkage = 'inventory.shrinkage',
    /** Reconstruir el inventario desde el ledger: los dos admins, cada uno en su alcance. */
    InventoryRebuild = 'inventory.rebuild',

    // Lo que ya existía y antes se decidía con `profile ===` sueltos
    BranchSelector = 'branch.selector',
    BranchServicesToggle = 'branch.services.toggle',
    AuthorizeWithPin = 'authorize.pin',
    HideLongRunningOrders = 'orders.hideLongRunning',
    OrdersPurge = 'orders.purge'
}

/**
 * Cómo aplica un permiso a un perfil:
 * - `any`: en cualquier sucursal.
 * - `ownBranches`: solo en las sucursales asignadas al usuario (`['all']` = todas).
 * - `ownBranchesSameDay`: además, solo sobre algo del día de hoy.
 */
export type CapabilityRule = 'any' | 'ownBranches' | 'ownBranchesSameDay';

const { Admin, BranchAdmin, Manager, Collaborator } = ProfileTypes;

/** Lo que el perfil no nombra, no lo puede. */
export const CAPABILITY_MATRIX: Record<Capability, Partial<Record<ProfileTypes, CapabilityRule>>> = {
    [Capability.ExpenseCaptureOperating]: {
        [Collaborator]: 'ownBranches',
        [Manager]: 'ownBranches',
        [BranchAdmin]: 'ownBranches',
        [Admin]: 'any'
    },
    // Decisión del negocio (§8.6): la nómina la capturan del gerente para arriba.
    [Capability.ExpenseCapturePayroll]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.ExpenseCaptureFixed]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.ExpenseEdit]: { [Manager]: 'ownBranchesSameDay', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.ExpenseCancel]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    // El colaborador ve la lista, pero acotada a lo suyo de hoy: `getExpenseListScope`.
    [Capability.ExpenseListView]: {
        [Collaborator]: 'ownBranchesSameDay',
        [Manager]: 'ownBranches',
        [BranchAdmin]: 'ownBranches',
        [Admin]: 'any'
    },
    [Capability.RecurringExpenseManage]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.ExpenseCategoriesManage]: { [Admin]: 'any' },
    [Capability.IncomeStatementView]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.IncomeStatementConsolidated]: { [Admin]: 'any' },

    [Capability.UsersManage]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    // Admin general y admin de sucursal tienen los MISMOS permisos; cambia el alcance
    // (decisión de Irving, 2026-09-30: "al final es admin de su propia sucursal").
    [Capability.UsersDelete]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.BranchEdit]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.BranchEditProtected]: { [Admin]: 'any' },
    [Capability.PromoCodesManage]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.PromoCodesManageGlobal]: { [Admin]: 'any' },
    [Capability.DashboardView]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    // La supervisión vive en su propia app desde el 2026-10-06; el admin solo la abre.
    [Capability.SupervisionAppOpen]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },

    // Plan de inventario F2 (decisión de Irving, 13-oct): la merma la captura cualquiera y la
    // autoriza con su PIN un gerente/admin de sucursal/admin; el PIN es el candado, no el rol.
    [Capability.InventoryCount]: {
        [Collaborator]: 'ownBranches',
        [Manager]: 'ownBranches',
        [BranchAdmin]: 'ownBranches',
        [Admin]: 'any'
    },
    [Capability.InventoryShrinkage]: {
        [Collaborator]: 'ownBranches',
        [Manager]: 'ownBranches',
        [BranchAdmin]: 'ownBranches',
        [Admin]: 'any'
    },
    [Capability.InventoryRebuild]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' },

    [Capability.BranchSelector]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.BranchServicesToggle]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.AuthorizeWithPin]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.HideLongRunningOrders]: { [Manager]: 'ownBranches', [BranchAdmin]: 'ownBranches', [Admin]: 'any' },
    [Capability.OrdersPurge]: { [BranchAdmin]: 'ownBranches', [Admin]: 'any' }
};

/** Quién pregunta. `profile` = con qué perfil OPERA (respeta "Ver como"). */
export interface PermissionSubject {
    profile?: ProfileTypes;
    branchOffices?: string[];
    uid?: string;
}

/** Sobre qué. Sin `branchOfficeId` la pregunta es "¿puede en general?". */
export interface PermissionContext {
    branchOfficeId?: string;
    /** La fecha de lo que se toca (un gasto): cuenta para `ownBranchesSameDay`. */
    date?: Date;
    now?: Date;
}

const sameLocalDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const coversBranch = (subject: PermissionSubject, branchOfficeId: string): boolean => {
    const branches = subject.branchOffices || [];
    return branches.includes('all') || branches.includes(branchOfficeId);
};

export const getCapabilityRule = (subject: PermissionSubject, capability: Capability): CapabilityRule | undefined =>
    subject.profile ? CAPABILITY_MATRIX[capability][subject.profile] : undefined;

export const can = (subject: PermissionSubject, capability: Capability, context: PermissionContext = {}): boolean => {
    const rule = getCapabilityRule(subject, capability);
    if (!rule) return false;
    if (rule === 'any') return true;
    if (context.branchOfficeId && !coversBranch(subject, context.branchOfficeId)) return false;
    if (rule === 'ownBranchesSameDay' && context.date) {
        return sameLocalDay(context.date, context.now || new Date());
    }
    return true;
};

/** Lo que el sujeto necesita del estado de auth (estructural: no importa el store). */
export interface PermissionAuthState {
    loggedUser?: { uid?: string; profile?: { profile?: ProfileTypes; branchOffices?: string[] } };
    profileSelected?: ProfileTypes;
    branchOfficeSelected?: { id?: string };
}

/**
 * Quién opera, a partir del estado de auth. PURO (sin store): se prueba aquí y lo
 * usan `getCurrentPermissionSubject` (servicios, handlers) y `usePermissionSubject`
 * (el render de una pantalla).
 *
 * 🚨 **"Ver como" previsualiza como alguien de LA SUCURSAL EN OPERACIÓN.** Cuando el
 * perfil seleccionado no es el real, las sucursales del sujeto son la del sidebar, no
 * las del admin (`['all']`). Hasta el 2026-09-30 se quedaban las reales: un admin
 * viendo como Admin de sucursal "administraba" a los usuarios de TODAS las
 * sucursales, y el Informe y el Dashboard le ofrecían "Todas". La previsualización
 * existe para reproducir lo que ve el staff; con `['all']` no reproducía nada.
 */
export const buildPermissionSubject = (auth: PermissionAuthState): PermissionSubject => {
    const realProfile = auth.loggedUser?.profile?.profile;
    const previewing = !!realProfile && !!auth.profileSelected && auth.profileSelected !== realProfile;
    const selected = auth.branchOfficeSelected?.id;
    return {
        profile: auth.profileSelected || realProfile,
        branchOffices: previewing ? (selected ? [selected] : []) : auth.loggedUser?.profile?.branchOffices || [],
        uid: auth.loggedUser?.uid
    };
};

/** ¿Ve todas las sucursales? (`'all'` en su asignación; previsualizando, nunca.) */
export const seesAllBranches = (subject: PermissionSubject): boolean => (subject.branchOffices || []).includes('all');

/**
 * Las sucursales que ve quien opera, de entre las cargadas: con `'all'` todas; si no,
 * solo las asignadas. Es lo que llena un selector de sucursal (Dashboard, Informe,
 * Usuarios, Cajas, los modales) para que "Ver como" enseñe lo mismo que ese perfil.
 */
export const visibleBranchOffices = <T extends { id?: string }>(subject: PermissionSubject, branchOffices: T[]): T[] =>
    seesAllBranches(subject) ? branchOffices : branchOffices.filter((branch) => !!branch.id && coversBranch(subject, branch.id));

/**
 * Qué perfiles puede asignar quien da de alta o edita un usuario. Nadie se da más
 * poder del que tiene. Decisión del negocio (§8.7): el admin de sucursal crea TODOS
 * los roles de su sucursal, incluido otro admin de sucursal; lo que no crea es lo
 * que vive fuera de ella (admin general, CEDIS).
 */
export const getAssignableProfiles = (subject: PermissionSubject): ProfileTypes[] => {
    if (subject.profile === Admin) return Object.values(ProfileTypes);
    if (subject.profile === BranchAdmin) return [BranchAdmin, Manager, Collaborator];
    return [];
};

/**
 * ¿Puede asignar estas sucursales? Todas tienen que ser suyas; "Todas" (`'all'`)
 * solo la asigna quien las tiene todas. Una lista vacía no es una asignación.
 */
export const canAssignBranches = (subject: PermissionSubject, branchOffices: string[]): boolean => {
    if (branchOffices.length === 0) return false;
    if (getCapabilityRule(subject, Capability.UsersManage) === 'any') return true;
    if (!getCapabilityRule(subject, Capability.UsersManage)) return false;
    const mine = subject.branchOffices || [];
    if (mine.includes('all')) return true;
    return branchOffices.every((branch) => branch !== 'all' && mine.includes(branch));
};

/**
 * ¿Puede ver, editar y dar de baja a este usuario? (§4.2, renglón Usuarios) El admin
 * general, a todos. El admin de sucursal, a quien tenga un perfil que él puede
 * asignar y TODAS sus sucursales dentro de las suyas: no ve a los de otras
 * sucursales ni a los que tienen "Todas".
 */
export const canManageUser = (
    subject: PermissionSubject,
    target: { profile?: ProfileTypes; branchOffices?: string[] }
): boolean => {
    const rule = getCapabilityRule(subject, Capability.UsersManage);
    if (!rule) return false;
    if (rule === 'any') return true;
    if (!target.profile || !getAssignableProfiles(subject).includes(target.profile)) return false;
    return canAssignBranches(subject, target.branchOffices || []);
};

/**
 * El gate del alta y la edición de un usuario (va en el servicio, no en el modal).
 * `previous` = el usuario tal como está hoy, al editar: también tiene que ser suyo.
 */
export const validateUserSave = (
    subject: PermissionSubject,
    values: { profile?: ProfileTypes; branchOffices?: string[] },
    previous?: { profile?: ProfileTypes; branchOffices?: string[] }
): string | null => {
    if (!getCapabilityRule(subject, Capability.UsersManage)) return 'Tu perfil no administra usuarios';
    if (previous && !canManageUser(subject, previous)) return 'Este usuario no es de tus sucursales';
    if (!values.profile || !getAssignableProfiles(subject).includes(values.profile)) {
        return 'No puedes asignar ese perfil';
    }
    if (!canAssignBranches(subject, values.branchOffices || [])) return 'Solo puedes asignar tus sucursales';
    return null;
};

/**
 * ¿Puede crear/editar/borrar este código? (§4.2, renglón Cupones) El admin general,
 * todos. El admin de sucursal, los limitados a SUS sucursales; los de "Todas las
 * sucursales" los ve pero no los toca.
 */
export const canManagePromoCode = (subject: PermissionSubject, code: { branchOfficesAvailable?: string[] }): boolean => {
    if (can(subject, Capability.PromoCodesManageGlobal)) return true;
    if (!getCapabilityRule(subject, Capability.PromoCodesManage)) return false;
    const branches = code.branchOfficesAvailable || [];
    if (branches.length === 0 || branches.includes('all')) return false;
    return branches.every((branch) => coversBranch(subject, branch));
};

/** ¿Lo ve en la lista? Los de "Todas" y los que aplican en alguna de sus sucursales. */
export const isPromoCodeVisible = (subject: PermissionSubject, code: { branchOfficesAvailable?: string[] }): boolean => {
    if (can(subject, Capability.PromoCodesManageGlobal)) return true;
    const branches = code.branchOfficesAvailable || [];
    return branches.includes('all') || branches.some((branch) => coversBranch(subject, branch));
};

export interface ExpenseListScope {
    branchOffices: 'all' | string[];
    /** Solo lo que capturó quien mira. */
    onlyOwn: boolean;
    /** Solo el día de hoy. */
    onlyToday: boolean;
}

/** Qué parte de la lista de gastos ve cada quien (tabla §4.1, renglón "Ver la lista"). */
export const getExpenseListScope = (subject: PermissionSubject): ExpenseListScope | null => {
    const rule = getCapabilityRule(subject, Capability.ExpenseListView);
    if (!rule) return null;
    if (rule === 'any') return { branchOffices: 'all', onlyOwn: false, onlyToday: false };
    const branches = subject.branchOffices || [];
    const branchOffices = branches.includes('all') ? 'all' : branches;
    if (subject.profile === Collaborator) return { branchOffices, onlyOwn: true, onlyToday: true };
    return { branchOffices, onlyOwn: false, onlyToday: false };
};

/**
 * Los perfiles que mandan en una sucursal: a quienes van los avisos de operación
 * (caja sin cerrar, servicio apagado) y quienes autorizan con su PIN.
 */
export const BRANCH_LEAD_PROFILES: ProfileTypes[] = [Admin, BranchAdmin, Manager];

/** Quién cobra nómina de una sucursal (plan de Gastos §8.3): su personal, no el admin general ni el CEDIS. */
export const PAYROLL_PROFILES: ProfileTypes[] = [BranchAdmin, Manager, Collaborator];
